import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';

/* global globalThis */


// ── Helpers ──
const fmtScore = (n) => (n == null ? '—' : n.toFixed(1));
const fmtPct = (n, d = 1) => {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(d)}%`;
};
const fmtPrice = (n) => (n == null ? '—' : n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }));
const rsiColor = (v) => {
  if (v == null) return '#3b82f6';
  if (v > 70) return '#ef4444';
  if (v < 30) return '#10b981';
  return '#3b82f6';
};
const localizeCopy = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.vi || value.en || '';
};

const thSx = {
  fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8',
  letterSpacing: '0.02em', py: 0.8,
  borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc',
};
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };
const clampTextSx = (lines = 2) => ({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lines,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const tablePrimaryTextSx = {
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#0f172a',
  lineHeight: 1.3,
};
const tableSecondaryTextSx = {
  fontSize: '0.62rem',
  color: '#94a3b8',
  lineHeight: 1.4,
};
const tableNumericTextSx = {
  fontSize: '0.78rem',
  fontWeight: 700,
  fontFamily: 'monospace',
  lineHeight: 1.2,
};
const tableSubNumericTextSx = {
  fontSize: '0.62rem',
  color: '#94a3b8',
  lineHeight: 1.35,
};

// Color helpers
const scoreColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 75) return '#10b981';
  if (s >= 50) return '#f59e0b';
  if (s >= 25) return '#f97316';
  return '#ef4444';
};

const labelColor = (label) => ({
  break_imminent: '#ef4444',
  break_ready: '#f59e0b',
  accumulating: '#3b82f6',
  none: '#94a3b8',
}[label] || '#94a3b8');

const phaseColor = (p) => ({
  accumulation: '#3b82f6',
  markup: '#10b981',
  distribution: '#ef4444',
  absorption: '#8b5cf6',
}[p] || '#94a3b8');

const readinessColor = (r) => {
  if (r == null) return '#94a3b8';
  if (r >= 0.65) return '#10b981';
  if (r >= 0.4) return '#f59e0b';
  return '#94a3b8';
};
const heatColor = (level, score) => {
  if (level === 'overheated') return '#ef4444';
  if (level === 'hot') return '#f97316';
  if (level === 'warm') return '#f59e0b';
  if (level === 'cool') return '#10b981';
  if (score == null) return '#94a3b8';
  if (score >= 70) return '#ef4444';
  if (score >= 45) return '#f59e0b';
  return '#10b981';
};
const stretchColor = (s) => {
  if (s == null) return '#94a3b8';
  if (Math.abs(s) > 20) return '#ef4444';
  if (Math.abs(s) > 10) return '#f59e0b';
  return '#10b981';
};
const earlyColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 55) return '#a855f7';
  if (s >= 35) return '#6366f1';
  return '#94a3b8';
};
const toneColor = (tone, fallback = '#94a3b8') => ({
  positive: '#10b981',
  positive_strong: '#059669',
  info: '#3b82f6',
  accent: '#8b5cf6',
  neutral: '#94a3b8',
  muted: '#64748b',
  caution: '#f59e0b',
  warning: '#f97316',
  danger: '#ef4444',
}[tone] || fallback);
const metricSnapshot = (snapshot, fallback) => ({
  text: snapshot?.text,
  detail: snapshot?.detail,
  tone: snapshot?.tone,
  color: toneColor(snapshot?.tone, fallback.color),
  value: snapshot?.detail ? null : fallback.value,
});

const distToPivotColor = (distPct) => {
  if (distPct == null) return '#64748b';
  if (distPct <= 3) return '#10b981';
  if (distPct <= 6) return '#f59e0b';
  return '#64748b';
};
const pctOrDash = (v, d = 1) => (v == null ? '—' : `${v.toFixed(d)}%`);
const readinessPctOrDash = (r) => (r == null ? '—' : `${(r * 100).toFixed(0)}%`);

const waitForRenderFrames = (count = 2) => new Promise((resolve) => {
  const tick = (remaining) => {
    if (remaining <= 0) {
      resolve();
      return;
    }
    globalThis.requestAnimationFrame(() => tick(remaining - 1));
  };
  tick(count);
});

const exportDrawerCapture = async ({ captureNode, scrollNode, fileName, backgroundColor = '#ffffff' }) => {
  if (!captureNode || !scrollNode) return;
  const paperNode = captureNode.closest('.MuiDrawer-paper');
  const toolbarNode = captureNode.querySelector('[data-export-header-toolbar="true"]');
  const restoreFns = [];

  try {
    if (toolbarNode instanceof HTMLElement) {
      const hadIgnore = toolbarNode.dataset.html2canvasIgnore != null;
      const prevVisibility = toolbarNode.style.visibility;
      const prevPointerEvents = toolbarNode.style.pointerEvents;
      restoreFns.push(() => {
        if (hadIgnore) toolbarNode.dataset.html2canvasIgnore = 'true';
        else delete toolbarNode.dataset.html2canvasIgnore;
        toolbarNode.style.visibility = prevVisibility;
        toolbarNode.style.pointerEvents = prevPointerEvents;
      });
      delete toolbarNode.dataset.html2canvasIgnore;
      toolbarNode.style.visibility = 'hidden';
      toolbarNode.style.pointerEvents = 'none';
    }

    if (paperNode instanceof HTMLElement) {
      const prevOverflow = paperNode.style.overflow;
      const prevMaxHeight = paperNode.style.maxHeight;
      const prevHeight = paperNode.style.height;
      restoreFns.push(() => {
        paperNode.style.overflow = prevOverflow;
        paperNode.style.maxHeight = prevMaxHeight;
        paperNode.style.height = prevHeight;
      });
      paperNode.style.overflow = 'visible';
      paperNode.style.maxHeight = 'none';
      paperNode.style.height = 'auto';
    }

    {
      const prevHeight = captureNode.style.height;
      const prevMinHeight = captureNode.style.minHeight;
      const prevOverflow = captureNode.style.overflow;
      restoreFns.push(() => {
        captureNode.style.height = prevHeight;
        captureNode.style.minHeight = prevMinHeight;
        captureNode.style.overflow = prevOverflow;
      });
      captureNode.style.height = 'auto';
      captureNode.style.minHeight = '0';
      captureNode.style.overflow = 'visible';
    }

    {
      const prevOverflowY = scrollNode.style.overflowY;
      const prevHeight = scrollNode.style.height;
      const prevMaxHeight = scrollNode.style.maxHeight;
      const prevFlex = scrollNode.style.flex;
      const prevScrollTop = scrollNode.scrollTop;
      restoreFns.push(() => {
        scrollNode.style.overflowY = prevOverflowY;
        scrollNode.style.height = prevHeight;
        scrollNode.style.maxHeight = prevMaxHeight;
        scrollNode.style.flex = prevFlex;
        scrollNode.scrollTop = prevScrollTop;
      });
      scrollNode.style.overflowY = 'visible';
      scrollNode.style.height = 'auto';
      scrollNode.style.maxHeight = 'none';
      scrollNode.style.flex = '0 0 auto';
    }

    captureNode.querySelectorAll('*').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const cs = globalThis.getComputedStyle(el);
      if (cs.minHeight && cs.minHeight !== '0px' && cs.minHeight !== 'auto') {
        const prevMinHeight = el.style.minHeight;
        restoreFns.push(() => {
          el.style.minHeight = prevMinHeight;
        });
        el.style.minHeight = '0';
      }
    });

    await waitForRenderFrames(2);
    globalThis.dispatchEvent(new Event('resize'));
    await waitForRenderFrames(3);

    const rect = captureNode.getBoundingClientRect();
    const exportWidth = Math.ceil(rect.width);
    const exportHeight = Math.ceil(Math.max(captureNode.scrollHeight, rect.height));

    const drawerCanvas = await html2canvas(captureNode, {
      backgroundColor,
      scale: 2,
      useCORS: true,
      width: exportWidth,
      height: exportHeight,
      windowWidth: window.innerWidth,
      windowHeight: Math.max(exportHeight, window.innerHeight),
      scrollX: 0,
      scrollY: 0,
    });

    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = drawerCanvas.width;
    finalCanvas.height = drawerCanvas.height;
    const ctx = finalCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(drawerCanvas, 0, 0);
    }

    const link = document.createElement('a');
    link.download = fileName;
    link.href = finalCanvas.toDataURL('image/png');
    link.click();
  } finally {
    while (restoreFns.length) {
      const restore = restoreFns.pop();
      try {
        restore?.();
      } catch {
        // best-effort restore — ignore
      }
    }
  }
};


const localizableCopy = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({ vi: PropTypes.string, en: PropTypes.string }),
]);

export {
  fmtScore,
  fmtPct,
  fmtPrice,
  rsiColor,
  localizeCopy,
  thSx,
  tdSx,
  clampTextSx,
  tablePrimaryTextSx,
  tableSecondaryTextSx,
  tableNumericTextSx,
  tableSubNumericTextSx,
  scoreColor,
  labelColor,
  phaseColor,
  readinessColor,
  heatColor,
  stretchColor,
  earlyColor,
  toneColor,
  metricSnapshot,
  distToPivotColor,
  pctOrDash,
  readinessPctOrDash,
  waitForRenderFrames,
  exportDrawerCapture,
  localizableCopy,
};
